import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { AlertTriangle, XCircle, Calendar, Package, Wallet, Landmark, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const ReservationOpsModal = ({ reservation, mode, open, onOpenChange, onDone }) => {
  const [step, setStep] = useState('config'); // config | preview | done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [paidSummary, setPaidSummary] = useState(null); // {paid_amount, total_installments, paid_installments}

  // Estados por modo
  const [cancelForm, setCancelForm] = useState({ by_agency: false, override_costs: '', reason: '', destination: 'refund', agency_reason: 'quorum' });
  const [dateForm, setDateForm] = useState({ new_date_id: '', new_room_type: '' });
  const [pkgForm, setPkgForm] = useState({ new_package_id: '', new_date_id: '', new_room_type: '' });
  const [creditForm, setCreditForm] = useState({ amount: '' });
  const [note, setNote] = useState('');

  // Data auxiliar
  const [packages, setPackages] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [newPackageObj, setNewPackageObj] = useState(null);

  const resetAll = useCallback(() => {
    setStep('config'); setLoading(false); setError(null); setPreview(null); setPaidSummary(null);
    setCancelForm({ by_agency: false, override_costs: '', reason: '', destination: 'refund', agency_reason: 'quorum' });
    setDateForm({ new_date_id: '', new_room_type: '' });
    setPkgForm({ new_package_id: '', new_date_id: '', new_room_type: '' });
    setCreditForm({ amount: '' });
    setNote('');
  }, []);

  useEffect(() => { if (open) resetAll(); }, [open, mode, reservation?.id, resetAll]);

  // Ao abrir o modal, calcula o valor efetivamente pago usando fontes de verdade em ordem de prioridade.
  useEffect(() => {
    if (!open || !reservation?.id) return;
    (async () => {
      const totalAmount = Number(reservation?.total_amount || reservation?.final_total || 0);
      const paidInst = Number(reservation?.paid_installments || 0);
      const totalInst = Number(reservation?.total_installments || 0);
      // Prioridade 1: campo amount_paid da própria reserva (source of truth vindo do webhook ASAAS)
      let paidAmount = Number(reservation?.amount_paid || reservation?.paid_amount || 0);

      // Prioridade 2: se paid_installments == total_installments (>0) → viagem totalmente paga
      if (!paidAmount && totalInst > 0 && paidInst >= totalInst) {
        paidAmount = totalAmount;
      }

      // Prioridade 3: pago parcial → proporcional (paid/total × total_amount)
      if (!paidAmount && totalInst > 0 && paidInst > 0 && paidInst < totalInst) {
        paidAmount = Math.round((totalAmount * paidInst / totalInst) * 100) / 100;
      }

      // Prioridade 4: consulta endpoint ASAAS (pode dar 0 se não houver payment record) — apenas se ainda 0
      if (!paidAmount) {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/asaas/installments/${reservation.id}`);
          if (r.data?.paid_amount > 0) paidAmount = Number(r.data.paid_amount);
        } catch (e) { /* ignora, mantém 0 */ }
      }

      setPaidSummary({
        paid_amount: paidAmount,
        total_amount: totalAmount,
        paid_installments: paidInst,
        total_installments: totalInst,
      });
    })();
  }, [open, reservation?.id, reservation?.paid_installments, reservation?.total_installments, reservation?.amount_paid, reservation?.total_amount]);

  // Carrega pacotes para transferência
  useEffect(() => {
    if (!open) return;
    if (mode === 'transfer-package' || mode === 'transfer-date') {
      axios.get(`${BACKEND_URL}/api/packages/`).then(r => setPackages(r.data || [])).catch(()=>{});
    }
  }, [open, mode]);

  // Datas disponíveis para o mesmo pacote (transferir data)
  useEffect(() => {
    if (mode !== 'transfer-date' || !reservation?.package_id) return;
    const p = packages.find(pk => pk.id === reservation.package_id);
    setAvailableDates((p?.dates || []).filter(d => d.id !== reservation.date_id && d.active !== false));
  }, [mode, packages, reservation]);

  // Datas do novo pacote (transferir pacote)
  useEffect(() => {
    if (mode !== 'transfer-package' || !pkgForm.new_package_id) return;
    const p = packages.find(pk => pk.id === pkgForm.new_package_id);
    setNewPackageObj(p);
    setAvailableDates((p?.dates || []).filter(d => d.active !== false));
  }, [pkgForm.new_package_id, packages, mode]);

  const runPreview = async () => {
    if (mode !== 'cancel') return; // preview só é obrigatório em cancelamento
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/cancellations/preview`, {
        reservation_id: reservation.id,
        by_agency: cancelForm.by_agency,
        override_costs: cancelForm.override_costs !== '' ? parseFloat(cancelForm.override_costs) : null,
        reason: cancelForm.reason || null,
      });
      setPreview(res.data);
      setStep('preview');
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao gerar cálculo.');
    } finally { setLoading(false); }
  };

  const confirmCancel = async () => {
    setLoading(true); setError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/cancellations/execute`, {
        reservation_id: reservation.id,
        by_agency: cancelForm.by_agency,
        override_costs: cancelForm.override_costs !== '' ? parseFloat(cancelForm.override_costs) : null,
        reason: cancelForm.reason || null,
        destination: cancelForm.destination,
        agency_reason: cancelForm.by_agency ? cancelForm.agency_reason : null,
      });
      setStep('done'); onDone?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao executar cancelamento.');
    } finally { setLoading(false); }
  };

  const confirmTransferDate = async () => {
    if (!dateForm.new_date_id) { setError('Selecione uma nova data.'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/reservations-ops/transfer-date`, {
        reservation_id: reservation.id, new_date_id: dateForm.new_date_id,
        new_room_type: dateForm.new_room_type || null,
        admin_note: note || null,
      });
      setStep('done'); onDone?.();
    } catch (e) { setError(e.response?.data?.detail || 'Erro na transferência.'); }
    finally { setLoading(false); }
  };

  const confirmTransferPackage = async () => {
    if (!pkgForm.new_package_id || !pkgForm.new_date_id) { setError('Selecione o novo pacote e a nova data.'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/reservations-ops/transfer-package`, {
        reservation_id: reservation.id,
        new_package_id: pkgForm.new_package_id,
        new_date_id: pkgForm.new_date_id,
        new_room_type: pkgForm.new_room_type || null,
        admin_note: note || null,
      });
      setStep('done'); onDone?.();
    } catch (e) { setError(e.response?.data?.detail || 'Erro na transferência.'); }
    finally { setLoading(false); }
  };

  const confirmTransferCredit = async (kind) => {
    setLoading(true); setError(null);
    try {
      const amt = creditForm.amount !== '' ? parseFloat(creditForm.amount) : null;
      await axios.post(`${BACKEND_URL}/api/reservations-ops/transfer-to-credit`, {
        reservation_id: reservation.id, kind, amount: amt, admin_note: note || null,
      });
      setStep('done'); onDone?.();
    } catch (e) { setError(e.response?.data?.detail || 'Erro na transferência para bolsa.'); }
    finally { setLoading(false); }
  };

  const modeTitle = {
    cancel: 'Cancelar Reserva',
    'transfer-date': 'Transferir para Outra Data',
    'transfer-package': 'Transferir para Outro Pacote',
    'transfer-credit-normal': 'Transferir para Bolsa de Crédito',
    'transfer-credit-limited': 'Transferir para Bolsa de Crédito Limitada',
  }[mode];

  const modeIcon = {
    cancel: <XCircle className="w-5 h-5 text-red-600" />,
    'transfer-date': <Calendar className="w-5 h-5 text-blue-600" />,
    'transfer-package': <Package className="w-5 h-5 text-indigo-600" />,
    'transfer-credit-normal': <Wallet className="w-5 h-5 text-emerald-600" />,
    'transfer-credit-limited': <Landmark className="w-5 h-5 text-amber-600" />,
  }[mode];

  // REGRA CRÍTICA: nunca usar total_amount. Sempre exibir o valor EFETIVAMENTE PAGO consultado no ASAAS.
  const paidValue = paidSummary ? Number(paidSummary.paid_amount || 0) : 0;
  const paidLabel = paidSummary && paidSummary.total_installments > 0
    ? `${paidSummary.paid_installments}/${paidSummary.total_installments} parcelas`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ops-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{modeIcon}{modeTitle}</DialogTitle>
          <DialogDescription>
            {reservation ? (
              <span className="text-xs">
                <strong>{reservation.customer_name}</strong> · {reservation.package_name} · {reservation.selected_date} · {formatBRL(paidValue)}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {error && <div className="p-2 bg-red-50 text-red-700 text-sm rounded" data-testid="ops-error">{error}</div>}

        {step === 'done' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded text-green-800" data-testid="ops-done">
            Operação concluída com sucesso.
            <div className="mt-3">
              <Button onClick={() => onOpenChange(false)} data-testid="ops-close-btn">Fechar</Button>
            </div>
          </div>
        )}

        {/* ---- MODO: CANCELAR ---- */}
        {step === 'config' && mode === 'cancel' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="by_agency" data-testid="by-agency-check"
                checked={cancelForm.by_agency}
                onChange={(e) => setCancelForm(f => ({ ...f, by_agency: e.target.checked }))} />
              <Label htmlFor="by_agency">Cancelamento pela Agência (sem multa)</Label>
            </div>
            {cancelForm.by_agency && (
              <div>
                <Label>Motivo</Label>
                <Select value={cancelForm.agency_reason} onValueChange={(v) => setCancelForm(f => ({ ...f, agency_reason: v }))}>
                  <SelectTrigger data-testid="agency-reason-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quorum">Quórum mínimo não atingido</SelectItem>
                    <SelectItem value="operational">Cancelamento operacional</SelectItem>
                    <SelectItem value="change">Alteração de operação</SelectItem>
                    <SelectItem value="force_majeure">Força maior</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Destino do valor</Label>
              <Select value={cancelForm.destination} onValueChange={(v) => setCancelForm(f => ({ ...f, destination: v }))}>
                <SelectTrigger data-testid="destination-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="refund">Estorno / Reembolso</SelectItem>
                  <SelectItem value="credit_normal">Bolsa de Crédito Normal</SelectItem>
                  <SelectItem value="credit_limited">Bolsa de Crédito Limitada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Custos não recuperáveis (override — opcional)</Label>
              <Input type="number" min="0" step="0.01" value={cancelForm.override_costs}
                onChange={(e) => setCancelForm(f => ({ ...f, override_costs: e.target.value }))}
                placeholder="Deixe em branco para usar Resultados Operacionais"
                data-testid="override-costs-input" />
            </div>
            <div>
              <Label>Observação (opcional)</Label>
              <Textarea value={cancelForm.reason} onChange={(e) => setCancelForm(f => ({ ...f, reason: e.target.value }))} data-testid="cancel-reason-input" />
            </div>
            <Button onClick={runPreview} disabled={loading} className="w-full bg-[#003366]" data-testid="run-preview-btn">
              {loading ? 'Calculando…' : 'Calcular Cancelamento'}
            </Button>
          </div>
        )}

        {step === 'preview' && mode === 'cancel' && preview && (
          <div className="space-y-3" data-testid="cancel-preview">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <p className="font-bold text-blue-900 mb-2">Regra aplicada: {preview.rule_applied}{preview.by_agency ? ' · sem multa (agência)' : ''}</p>
              <ul className="text-xs space-y-1 text-gray-700">
                <li><strong>Valor efetivamente pago:</strong> {formatBRL(preview.paid_amount)}</li>
                <li><strong>Custos não recuperáveis:</strong> {formatBRL(preview.non_recoverable_costs)}</li>
                <li><strong>Taxa administrativa:</strong> {formatBRL(preview.administrative_fee)}</li>
                <li><strong>Saldo remanescente:</strong> {formatBRL(preview.remaining_balance)}</li>
                <li><strong>Prazo até a viagem:</strong> {preview.days_until_trip} dia(s)</li>
                <li><strong>Retenção:</strong> {preview.retention_percentage}% = {formatBRL(preview.retention_value)}</li>
              </ul>
              <p className="text-lg font-bold mt-3 text-emerald-800" data-testid="refund-eligible">
                Valor elegível para {cancelForm.destination === 'refund' ? 'restituição' : 'crédito'}: {formatBRL(preview.refund_eligible)}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('config')}>Voltar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={confirmCancel} disabled={loading} data-testid="confirm-cancel-btn">
                {loading ? 'Executando…' : 'Confirmar Cancelamento'}
              </Button>
            </div>
          </div>
        )}

        {/* ---- MODO: TRANSFERIR DATA ---- */}
        {step === 'config' && mode === 'transfer-date' && (
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded text-sm">
              <p><strong>Pacote:</strong> {reservation?.package_name}</p>
              <p><strong>Data atual:</strong> {reservation?.selected_date}</p>
              <p><strong>Passageiros:</strong> {(reservation?.travelers || reservation?.all_travelers || []).length || reservation?.number_of_people || 1} pessoa(s) — TODOS serão transferidos</p>
            </div>

            {(reservation?.travelers || reservation?.all_travelers || []).length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm" data-testid="transfer-date-travelers-list">
                <p className="font-semibold text-blue-900 mb-2">Passageiros a transferir ({(reservation?.travelers || reservation?.all_travelers || []).length}):</p>
                <ul className="space-y-1">
                  {(reservation?.travelers || reservation?.all_travelers || []).map((t, i) => (
                    <li key={i} className="text-xs text-blue-800" data-testid={`transfer-date-traveler-${i}`}>
                      <strong>{i === 0 ? 'Titular' : `Acompanhante ${i}`}:</strong> {t.full_name || t.fullName || t.name || '(sem nome)'} — CPF {t.cpf || '—'}
                      {t.birth_date && ` — Nasc. ${t.birth_date}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Label>Nova Data</Label>
              <Select value={dateForm.new_date_id} onValueChange={(v) => setDateForm(f => ({ ...f, new_date_id: v }))}>
                <SelectTrigger data-testid="new-date-select"><SelectValue placeholder="Selecione a nova data" /></SelectTrigger>
                <SelectContent>
                  {availableDates.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de quarto (opcional — mantém o atual se vazio)</Label>
              <Select value={dateForm.new_room_type} onValueChange={(v) => setDateForm(f => ({ ...f, new_room_type: v }))}>
                <SelectTrigger data-testid="new-date-room-select"><SelectValue placeholder="Manter atual" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Compartilhado</SelectItem>
                  <SelectItem value="private">Privativo</SelectItem>
                  <SelectItem value="couple">Casal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nota administrativa (opcional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button className="w-full bg-[#003366]" onClick={confirmTransferDate} disabled={loading} data-testid="confirm-transfer-date-btn">
              {loading ? 'Transferindo…' : 'Confirmar Transferência de Data'}
            </Button>
          </div>
        )}

        {/* ---- MODO: TRANSFERIR PACOTE ---- */}
        {step === 'config' && mode === 'transfer-package' && (
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded text-sm">
              <p><strong>Pacote atual:</strong> {reservation?.package_name}</p>
              <p><strong>Data atual:</strong> {reservation?.selected_date}</p>
              <p><strong>Passageiros:</strong> {(reservation?.travelers || reservation?.all_travelers || []).length || reservation?.number_of_people || 1} pessoa(s) — TODOS serão transferidos</p>
              <p><strong>Valor efetivamente pago:</strong> {formatBRL(paidValue)} {paidLabel && <span className="text-xs text-gray-500">({paidLabel})</span>}</p>
            </div>

            {/* Lista completa de passageiros - garante que admin veja TODOS antes de transferir */}
            {(reservation?.travelers || reservation?.all_travelers || []).length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm" data-testid="transfer-travelers-list">
                <p className="font-semibold text-blue-900 mb-2">Passageiros a transferir ({(reservation?.travelers || reservation?.all_travelers || []).length}):</p>
                <ul className="space-y-1">
                  {(reservation?.travelers || reservation?.all_travelers || []).map((t, i) => (
                    <li key={i} className="text-xs text-blue-800 flex items-center gap-2" data-testid={`transfer-traveler-${i}`}>
                      <input type="checkbox" checked disabled className="cursor-not-allowed" data-testid={`transfer-traveler-check-${i}`} />
                      <span><strong>{i === 0 ? 'Titular' : `Acompanhante ${i}`}:</strong> {t.full_name || t.fullName || t.name || '(sem nome)'} — CPF {t.cpf || '—'}
                      {t.birth_date && ` — Nasc. ${t.birth_date}`}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-blue-700 mt-2 italic">Todos os passageiros da reserva são transferidos automaticamente.</p>
              </div>
            )}
            <div>
              <Label>Novo Pacote</Label>
              <Select value={pkgForm.new_package_id} onValueChange={(v) => setPkgForm(f => ({ ...f, new_package_id: v, new_date_id: '' }))}>
                <SelectTrigger data-testid="new-package-select"><SelectValue placeholder="Selecione o pacote" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {packages.filter(p => p.id !== reservation?.package_id).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pkgForm.new_package_id && (
              <>
              <div>
                <Label>Nova Data</Label>
                <Select value={pkgForm.new_date_id} onValueChange={(v) => setPkgForm(f => ({ ...f, new_date_id: v }))}>
                  <SelectTrigger data-testid="new-package-date-select"><SelectValue placeholder="Selecione a data" /></SelectTrigger>
                  <SelectContent>
                    {availableDates.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.date}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de quarto (opcional — mantém o atual se vazio)</Label>
                <Select value={pkgForm.new_room_type} onValueChange={(v) => setPkgForm(f => ({ ...f, new_room_type: v }))}>
                  <SelectTrigger data-testid="new-pkg-room-select"><SelectValue placeholder="Manter atual" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">Compartilhado</SelectItem>
                    <SelectItem value="private">Privativo</SelectItem>
                    <SelectItem value="couple">Casal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </>
            )}
            {newPackageObj && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                <p><strong>Novo pacote:</strong> {newPackageObj.name}</p>
                <p><strong>Preço base:</strong> {formatBRL(newPackageObj.price)}</p>
                <div className="mt-2 text-xs text-blue-800 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Novo contrato será gerado — cliente precisa aceitar para confirmar.
                </div>
                {parseFloat(newPackageObj.price) !== paidValue && (
                  <p className="text-xs mt-1 text-amber-700 flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" /> Diferença: {formatBRL(parseFloat(newPackageObj.price) - paidValue)}
                  </p>
                )}
              </div>
            )}
            <div>
              <Label>Nota administrativa (opcional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button className="w-full bg-[#003366]" onClick={confirmTransferPackage} disabled={loading} data-testid="confirm-transfer-package-btn">
              {loading ? 'Transferindo…' : 'Confirmar Transferência de Pacote'}
            </Button>
          </div>
        )}

        {/* ---- MODO: TRANSFERIR PARA BOLSA ---- */}
        {step === 'config' && (mode === 'transfer-credit-normal' || mode === 'transfer-credit-limited') && (
          <div className="space-y-3">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
              <p><strong>Reserva:</strong> {reservation?.package_name} · {reservation?.selected_date}</p>
              <p><strong>Cliente:</strong> {reservation?.customer_name} · CPF {reservation?.customer_cpf}</p>
              <p className="mt-2 flex items-baseline gap-2" data-testid="credit-paid-summary">
                <strong>Valor efetivamente pago:</strong>
                <span className="text-lg font-bold text-emerald-700">{formatBRL(paidValue)}</span>
                {paidLabel && <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">{paidLabel}</span>}
              </p>
              {paidSummary?.total_amount > 0 && paidSummary.total_amount !== paidSummary.paid_amount && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Total contratado: {formatBRL(paidSummary.total_amount)} — <strong>a bolsa será gerada com o valor PAGO acima, nunca o total.</strong>
                </p>
              )}
            </div>
            <div>
              <Label>Valor a transferir para a bolsa</Label>
              <Input type="number" min="0" step="0.01"
                value={creditForm.amount}
                onChange={(e) => setCreditForm({ amount: e.target.value })}
                placeholder={`Padrão: ${formatBRL(paidValue)}`}
                data-testid="credit-amount-input" />
              <p className="text-xs text-gray-500 mt-1">Se em branco, transfere o valor efetivamente pago.</p>
            </div>
            <div>
              <Label>Nota administrativa (opcional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button className={`w-full ${mode === 'transfer-credit-limited' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              onClick={() => confirmTransferCredit(mode === 'transfer-credit-limited' ? 'limited' : 'normal')}
              disabled={loading}
              data-testid="confirm-transfer-credit-btn">
              {loading ? 'Transferindo…' : `Confirmar Transferência para Bolsa ${mode === 'transfer-credit-limited' ? 'Limitada' : 'Normal'}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReservationOpsModal;
